<template>
  <div class="demo">
    <div class="labels">
      <div ref="labelLeft" class="left d-flex align-items-center justify-content-center adobe-clean-bold" v-html="choices[0].name"></div>
    </div>

    <canvas id="liquid" ref="canvas"></canvas>

    <div class="labels">
      <div ref="labelRight" class="right d-flex align-items-center justify-content-center adobe-clean-bold" v-html="choices[1].name"></div>
    </div>

    <transition name="fade">
      <div class="progress-container" v-show="progressPct > 0">
        <div class="wrapper d-flex align-items-center justify-content-center">
          <progress-ring
            id="progress" ref="prog"
            :width="progressSize"
            :height="progressSize"
            :percent="progressPct"
            :rgbaTrackColor="progressTrackColor"
            :rgbaRingColor="progressRingColor"
            @onComplete="onProgressComplete" />
        </div>
      </div>
    </transition>

    <div class="intro d-flex flex-column align-items-center justify-content-center" ref="intro" v-if="introActive">
      <div class="intro-item">
        <next-mobile-decade class="nmd" />
      </div>
      <motion-icon class="icon intro-item" :complete="iconComplete" :active="iconActive" :isButton="true" />
      <h3 class="adobe-clean-serif intro-item text-center">Tell us what interests you most.</h3>
    </div>

    <div class="hint d-flex align-items-center justify-content-center" ref="hint">
      <h5>HINT: <span>Tilt and hold.</span></h5>
    </div>

    <div v-if="isComplete" class="selection" :class="selectedChoice.class" :style="borderGradient">
      <div class="selection-container d-flex flex-column align-items-center justify-content-center">
        <motion-icon class="icon select-item" :complete="iconComplete" :white="selectedChoice.class.length > 0 ? true : false"  :active="true" :isButton="true" />
        <h3 class="knockout-50 select-item">COMPLETED</h3>
        <p class="text-center adobe-clean-serif text-italic select-item">
          Great insight. Your report now focuses on an interest in <span class="bold adobe-clean-xbold">{{selectedChoice.short}}</span> data.
        </p>
      </div>
      <div v-if="outroActive"
        ref="outro"
        class="outro d-flex flex-column align-items-center justify-content-center user-select-none"
        @click="$router.push('scroll')">
          <div class="outro-item">
            <next-mobile-decade class="nmd" :fill="selectedChoice.class.length > 0 ? '#ffffff' : '#000000'" />
          </div>
          <p class="adobe-clean-serif text-center outro-item">
            Personalization starts with customer input. Choices yield power.
          </p>
          <div class="cta d-flex flex-column justify-content-end outro-item">
            <span>Tap to continue</span>
          </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { TweenMax, TimelineMax, Power2, Power3, Elastic } from 'gsap'
import { Math as ThreeMath } from 'three'
import { shadeBlendConvert } from '@/lib/funcs'
import { industryTypes } from '@/lib/consts'
import Vertex from '@/lib/Vertex'
import ProgressRing from './ProgressRing'
import Noise from './Noise'
import Chevron from '@/components/svg/Chevron'
import MotionIcon from '@/components/svg/MotionIcon'
import NextMobileDecade from '@/components/svg/NextMobileDecade'
require('gsap/ColorPropsPlugin')

export default {
  name: 'demo-motion',
  components: {
    ProgressRing,
    Chevron,
    MotionIcon,
    NextMobileDecade,
    Noise
  },
  props: ['isPortrait'],
  watch: {
    isPortrait (portraitMode) {
      if (!portraitMode) {
        this.shouldRender = false
        this.cancelAutoRipple()
        if (window.DeviceMotionEvent && window.DeviceOrientationEvent) {
          window.removeEventListener('devicemotion', this.onDeviceMotion, false)
          window.removeEventListener('deviceorientation', this.rotationHandler, false)
        }
      } else {
        if (!this.isComplete) {
          this.transX = 0
          this.baseX = 0
          this.progressPct = 0
        }

        if (window.DeviceMotionEvent && window.DeviceOrientationEvent) {
          window.addEventListener('devicemotion', this.onDeviceMotion, false)
          if (!this.isComplete) {
            window.addEventListener('deviceorientation', this.rotationHandler, false)
          }
        }
        this.shouldRender = true
        this.update()
      }
    }
  },
  data () {
    return {
      idx: 1,
      introActive: true,
      introDelay: 4,
      outroActive: false,
      // isPortrait: true,
      isComplete: false,
      isMarketing: true,
      shouldRender: true,
      rafID: 0,
      fullWidth: 0,
      fullHeight: 0,
      ctx: undefined,
      progressTrackColor: '',
      progressRingColor: '',
      progressPct: 0,
      mainGradient: undefined,
      middleGradient: undefined,
      outerGradient: undefined,
      vertices: [],
      diffPt: [],
      autoDiff: 0,
      maxDiff: 750,
      verNum: 250,
      xx: 150,
      dd: 20,
      choices: [
        {
          selected: false,
          id: industryTypes.mktg.id,
          name: industryTypes.mktg.displayName,
          short: industryTypes.mktg.shortName,
          class: industryTypes.mktg.demoClass
        },
        {
          selected: false,
          id: industryTypes.it.id,
          name: industryTypes.it.displayName,
          short: industryTypes.it.shortName,
          class: industryTypes.it.demoClass
        }
      ],
      colors: {
        gradient1: '#7FE2CC',
        gradient2: '#66b5a3',
        gradient3: '#FFA3A3',
        gradient4: '#E7A4E8',
        gradient5: '#B9FDFB',
        gradient6: '#C8A1FD'
      },
      colorSets: [
        {
          primary: '#7FE2CC',
          secondary: shadeBlendConvert( -0.2, '#7FE2CC' ),
          middleP: shadeBlendConvert( 0.2, '#7FE2CC' ),
          middleS: shadeBlendConvert( 0.2, '#66b5a3' ),
          outerP: shadeBlendConvert( 0.5, '#7FE2CC' ),
          outerS: shadeBlendConvert( 0.5, '#66b5a3' )
        },
        {
          primary: '#FFA3A3',
          secondary: '#E7A4E8',
          middleP: shadeBlendConvert( 0.2, '#FFA3A3' ),
          middleS: shadeBlendConvert( 0.2, '#E7A4E8' ),
          outerP: shadeBlendConvert( 0.5, '#FFA3A3' ),
          outerS: shadeBlendConvert( 0.5, '#E7A4E8' )
        },
        {
          primary: '#B9FDFB',
          secondary: '#C8A1FD',
          middleP: shadeBlendConvert( 0.2, '#B9FDFB' ),
          middleS: shadeBlendConvert( 0.2, '#C8A1FD' ),
          outerP: shadeBlendConvert( 0.5, '#B9FDFB' ),
          outerS: shadeBlendConvert( 0.5, '#C8A1FD' )
        }
      ],
      colorTweenCount: 0,
      liquidPosition: 0.5,
      transX: 0,
      minTransX: 0,
      maxTransX: 0,
      baseX: 0,
      minBaseX: 0,
      maxBaseX: 0,
      autoRippleActive: false,
      autoRippleInterval: 1400,
      rippleInt: 0,
      lastRipple: 0,
      interval: 0,
      accelData: {
        x: 0,
        y: 0,
        z: 0
      },
      accelGravData: {
        x: 0,
        y: 0,
        z: 0
      },
      rotationData: {
        a: 0,
        b: 0,
        g: 0
      },
      startPt: {
        x: screen.availWidth / 2,
        y: -40
      },
      controlPt: {
        x: screen.availWidth / 2,
        y: screen.availHeight / 2
      },
      endPt: {
        x: screen.availWidth / 2,
        y: screen.availHeight + 40
      },
      tlRight: new TimelineMax({paused: true}),
      tlLeft: new TimelineMax({paused: true}),
      timelines: [],
      tweenTargets: [],
      iconActive: true,
      motionCount: 0
    }
  },
  computed: {
    checkColor () {
      return this.baseX < 0 ? 'rgb(250, 250, 250)' : 'rgb(0, 0, 0)'
    },
    selectedChoice () {
      return this.choices.find( c => c.selected )
    },
    scaleFactor () {
      if ('devicePixelRatio' in window) {
        if (window.devicePixelRatio > 1) {
          return window.devicePixelRatio
        }
      }
      return 1
    },
    progressSize () {
      return ThreeMath.clamp( this.fullWidth * 0.6, 200, 360 )
    },
    borderGradient () {
      if ( this.selectedChoice.class.length === 0 ) {
        return `border-color: ${this.colors.gradient1};
          border-image: linear-gradient(135deg, ${this.colors.gradient2} 0%, ${this.colors.gradient1} 100%) 10;`
      }
    },
    ...mapState({
      didViewReport: state => state.report.didView
    }),
    ...mapState('demo1', {
      iconComplete: state => state.complete
    })
  },
  methods: {
    onDeviceMotion ( eventData ) {
      this.accelerationHandler( eventData.acceleration )
      this.accelerationGravHandler( eventData.accelerationIncludingGravity )
      this.interval = eventData.interval
    },
    accelerationHandler ( acceleration ) {
      this.accelData.x = acceleration.x && acceleration.x.toFixed(3)
      this.accelData.y = acceleration.y && acceleration.y.toFixed(3)
      this.accelData.z = acceleration.z && acceleration.z.toFixed(3)

      // Light X acceleration trigger ripple at a random Y position of available vertical space
      if ( Math.abs( this.accelData.x ) > 0.5 ) {
        let randY = Math.floor(Math.random() * Math.floor(this.fullHeight))
        this.triggerRipple(randY)
      }

      // Hard Z acceleration trigger ripple in middle 3/4 of vertical space
      if ( Math.abs( this.accelData.z ) > 6 ) {
        let randY = Math.floor( Math.random() * Math.floor( this.fullHeight / 2 ) ) + Math.floor( this.fullHeight / 4 )
        this.triggerRipple( randY, 3 )
      }
    },
    accelerationGravHandler ( acceleration ) {
      this.accelGravData.x = acceleration.x && acceleration.x.toFixed( 3 )
      this.accelGravData.y = acceleration.y && acceleration.y.toFixed( 3 )
      this.accelGravData.z = acceleration.z && acceleration.z.toFixed( 3 )
    },
    rotationHandler ( rotation ) {
      this.rotationData.a = rotation.alpha && rotation.alpha.toFixed( 3 )
      this.rotationData.b = rotation.beta && rotation.beta.toFixed( 3 )
      this.rotationData.g = rotation.gamma && rotation.gamma.toFixed( 3 )
    },
    triggerRipple ( yPos, factor = 1 ) {
      let now = new Date().getTime()
      if ( this.lastRipple === 0 || now - this.lastRipple > 50 ) {
        this.autoDiff = this.maxDiff * factor
        this.xx = 1 + Math.floor( ( this.verNum ) * yPos / this.fullHeight )
        this.diffPt[this.xx] = this.autoDiff
        this.lastRipple = now
        this.cancelAutoRipple()
      }
    },
    autoRipple ( yPos ) {
      this.autoDiff = this.maxDiff * 0.85
      this.xx = 1 + Math.floor( ( this.verNum ) * yPos / this.fullHeight )
      this.diffPt[this.xx] = this.autoDiff
    },
    startAutoRipple () {
      this.rippleInt = setInterval( () => {
        let y = Math.floor( Math.random() * Math.floor( this.fullHeight ) )
        this.autoRipple( y )
      }, this.autoRippleInterval )
      this.autoRippleActive = true
    },
    cancelAutoRipple () {
      clearInterval( this.rippleInt )
      this.autoRippleActive = false
    },
    onMouseDown ( e ) {
      let mouseX = e.pageX
      let mouseY = e.pageY

      if ( this.fullWidth / 2 - mouseX < 50 && this.fullWidth / 2 - mouseX > -50 ) {
        this.autoDiff = this.maxDiff
        if ( mouseX < this.$refs.canvas.width - 2 ) {
          this.xx = 1 + Math.floor( ( this.verNum - 2 ) * mouseY / this.fullHeight )
          this.diffPt[this.xx] = this.autoDiff
        }
      }
    },
    initCanvas () {
      this.fullWidth = window.screen ? screen.availWidth : window.innerWidth
      // this.fullHeight = window.screen ? screen.availHeight : window.innerHeight
      this.fullHeight = window.innerHeight
      this.$refs.canvas.width = this.fullWidth
      this.$refs.canvas.height = this.fullHeight
      for ( let i = 0; i < this.verNum; i++ ) {
        this.vertices[i] = new Vertex(
          this.$refs.canvas.width * this.liquidPosition,
          this.$refs.canvas.height / ( this.verNum - 1 ) * i,
          this.$refs.canvas.width * this.liquidPosition,
          this.$refs.canvas.height / 2
        )
      }
      this.initDiffPt()
    },
    onResize () {
      this.fullWidth = ( window.screen ) ? screen.availWidth : window.innerWidth
      // this.fullHeight = ( window.screen ) ? screen.availHeight : window.innerHeight
      this.fullHeight = window.innerHeight
      if ( this.isPortrait ) {
        this.$refs.canvas.width = this.fullWidth
        this.$refs.canvas.height = this.fullHeight
        this.minTransX = -(this.fullWidth / 2 - this.fullWidth * 0.1)
        this.maxTransX = this.fullWidth / 2 - this.fullWidth * 0.1
        this.minBaseX = -(this.fullWidth / 2 - this.fullWidth * 0.025)
        this.maxBaseX = this.fullWidth / 2 - this.fullWidth * 0.025
        this.startPt.x = this.fullWidth / 2
        this.startPt.y = -40
        this.controlPt.x = this.fullWidth / 2
        this.controlPt.y = this.fullHeight / 2
        this.endPt.x = this.fullWidth / 2
        this.endPt.y = this.fullHeight + 40
      }
    },
    _getQBezierValue ( t, p1, p2, p3 ) {
      let iT = 1 - t
      return iT * iT * p1 + 2 * iT * t * p2 + t * t * p3
    },
    getQuadraticCurvePoint ( startX, startY, cpX, cpY, endX, endY, position ) {
      return {
        x: this._getQBezierValue( position, startX, cpX, endX ),
        y: this._getQBezierValue( position, startY, cpY, endY )
      }
    },
    initDiffPt () {
      for ( let i = 0; i < this.verNum; i++ ) {
        this.diffPt[i] = 0
      }
    },
    update () {
      if ( this.shouldRender ) {
        this.rafID = requestAnimationFrame(() => {
          this.update()
        })

        if ( this.isPortrait ) {
          // Tilt gamma updates translation X and bezier control point
          if ( this.rotationData.g > 2 ) {
            this.transX -= Math.floor( Math.abs( this.rotationData.g ) / 3 )
            this.baseX -= Math.floor( Math.abs( this.rotationData.g ) / 6 )
          } else if ( this.rotationData.g < -2 ) {
            this.transX += Math.floor( Math.abs( this.rotationData.g ) / 3 )
            this.baseX += Math.floor( Math.abs( this.rotationData.g ) / 6 )
          } else if ( this.rotationData.g < 2 && this.rotationData.g > -2 ) {
            if ( this.baseX >= 2 ) {
              this.baseX -= 2
            } else if ( this.baseX <= -2 ) {
              this.baseX += 2
            } else {
              this.baseX = 0
            }
            if ( this.transX >= 3 ) {
              this.transX -= 3
            } else if ( this.transX <= -3 ) {
              this.transX += 3
            } else {
              this.transX = 0
            }
          }

          // Limit left/right travel
          this.transX = ThreeMath.clamp( this.transX, -( this.fullWidth / 2 - this.fullWidth * 0.1 ), ( this.fullWidth / 2 - this.fullWidth * 0.1 ) )
          this.baseX = ThreeMath.clamp( this.baseX, -( this.fullWidth / 2 - this.fullWidth * 0.025 ), ( this.fullWidth / 2 - this.fullWidth * 0.025 ) )

          // Update bezier points
          let cpx = this.fullWidth / 2 + this.transX
          let spx = this.fullWidth / 2 + this.baseX
          let epx = this.fullWidth / 2 + this.baseX
          this.startPt.x = spx
          this.controlPt.x = cpx
          this.endPt.x = epx

          // Update progress ring
          if ( ( this.transX <= this.minTransX && this.baseX <= this.minBaseX ) || ( this.transX >= this.maxTransX && this.baseX >= this.maxBaseX ) ) {
            this.progressPct = ThreeMath.clamp( this.progressPct + 0.01, 0, 1 )
          } else {
            this.progressPct = ThreeMath.clamp( this.progressPct - 0.035, 0, 1 )
          }

          this.progressTrackColor = this.transX > 0 ? 'rgba( 0, 0, 0, 0.1 )' : 'rgba( 255, 255, 255, 0.25 )'
          this.progressRingColor = this.transX > 0 ? this.colors.gradient1 : 'rgba( 255, 255, 255, 1 )'

          if ( this.baseX < 0 ) {
            let tweenProg = Math.abs( this.baseX / this.minBaseX )
            this.tlRight.progress( tweenProg )
          } else {
            let tweenProg = Math.abs( this.baseX / this.maxBaseX )
            this.tlLeft.progress( tweenProg )
          }
        }

        // Clear canvas, set initial point
        this.ctx.clearRect( 0, 0, this.fullWidth, this.fullHeight )
        this.autoDiff -= this.autoDiff * 0.9
        this.diffPt[this.xx] = this.autoDiff

        // Calculate up
        for ( let i = this.xx - 1; i > 0; i-- ) {
          let d = this.xx - i
          if (d > this.dd) d = this.dd
          this.diffPt[i] -= ( this.diffPt[i] - this.diffPt[i + 1] ) * ( 1 - 0.0075 * d )
        }

        // Calculate down
        for ( let i = this.xx + 1; i < this.verNum; i++ ) {
          let d = i - this.xx
          if ( d > this.dd ) d = this.dd
          this.diffPt[i] -= ( this.diffPt[i] - this.diffPt[i - 1] ) * ( 1 - 0.0075 * d )
        }

        // X coordinate points update
        for ( let i = 0; i < this.vertices.length; i++ ) {
          let pos = i / ( this.vertices.length - 1 )
          let newPt = this.getQuadraticCurvePoint( this.startPt.x, this.startPt.y, this.controlPt.x, this.controlPt.y, this.endPt.x, this.endPt.y, pos )
          this.vertices[i].baseX = newPt.x
          this.vertices[i].baseY = newPt.y
          this.vertices[i].updateX( this.diffPt[i] )
          this.vertices[i].updateY( this.diffPt[i] )
        }

        // Tone it down a notch
        let now = new Date().getTime()
        if ( now - this.lastRipple > this.autoRippleInterval / 2 && !this.autoRippleActive ) {
          this.startAutoRipple()
        }

        this.draw()

        if ( this.$refs.prog ) {
          this.$refs.prog.update()
        }
      }
    },
    draw () {
      this.mainGradient = this.ctx.createLinearGradient( this.fullWidth * this.liquidPosition, 0, this.fullWidth, this.fullHeight )
      this.mainGradient.addColorStop( 0, this.colors.gradient1 )
      this.mainGradient.addColorStop( 1, this.colors.gradient2 )

      this.middleGradient = this.ctx.createLinearGradient( this.fullWidth * this.liquidPosition, 0, this.fullWidth, this.fullHeight )
      this.middleGradient.addColorStop( 0, this.colors.gradient3 )
      this.middleGradient.addColorStop( 1, this.colors.gradient4 )

      this.outerGradient = this.ctx.createLinearGradient( this.fullWidth * this.liquidPosition, 0, this.fullWidth, this.fullHeight )
      this.outerGradient.addColorStop( 0, this.colors.gradient5 )
      this.outerGradient.addColorStop( 1, this.colors.gradient6 )

      // lightest outer
      this.ctx.beginPath()
      this.ctx.fillStyle = this.outerGradient
      this.ctx.moveTo(this.fullWidth * this.liquidPosition, 0)
      this.ctx.lineTo(this.vertices[0].x - 10, this.vertices[0].y)
      for (let i = 1; i < this.vertices.length; i++) {
        this.ctx.lineTo(this.vertices[i].x - 10, this.vertices[i].y + 36)
      }
      this.ctx.lineTo(this.fullWidth, this.fullHeight)
      this.ctx.lineTo(this.fullWidth, 0)
      this.ctx.fill()

      // lighter middle
      this.ctx.beginPath()
      this.ctx.fillStyle = this.middleGradient
      this.ctx.moveTo(this.vertices[0].x, 0)
      this.ctx.lineTo(this.vertices[0].x, this.vertices[0].y)
      for (let i = 1; i < this.vertices.length; i++) {
        this.ctx.lineTo(this.vertices[i].x, this.vertices[i].y + 16)
      }
      this.ctx.lineTo(this.fullWidth, this.fullHeight)
      this.ctx.lineTo(this.fullWidth, 0)
      this.ctx.fill()

      // main fill
      this.ctx.beginPath()
      this.ctx.fillStyle = this.mainGradient
      this.ctx.moveTo(this.fullWidth * this.liquidPosition + 10, 0)
      this.ctx.lineTo(this.vertices[0].x + 10, this.vertices[0].y)
      for (let i = 1; i < this.vertices.length; i++) {
        this.ctx.lineTo(this.vertices[i].x + 10, this.vertices[i].y)
      }
      this.ctx.lineTo(this.fullWidth, this.fullHeight)
      this.ctx.lineTo(this.fullWidth, 0)
      this.ctx.fill()
    },
    onProgressComplete () {
      if ( !this.isComplete ) {
        this.lockSelection()
      }
    },
    lockSelection () {
      if ( this.baseX > 0 ) {
        this.choices[0].selected = true
      } else {
        this.choices[1].selected = true
      }

      this.isComplete = true
      this.$store.dispatch( 'setDemoComplete', this.idx )
      this.$store.dispatch( 'setIndustry', this.selectedChoice.id )

      // window.removeEventListener('devicemotion', this.onDeviceMotion, false)
      // window.removeEventListener( 'resize', this.onResize )
      window.removeEventListener( 'deviceorientation', this.rotationHandler, false )

      TweenMax.to( this.$refs.hint, 0.75, {
        opacity: 0,
        y: -100,
        ease: Power2.easeIn
      } )

      let tlSelection = new TimelineMax( { onComplete: this.showInterstitial } )
      tlSelection.add( TweenMax.to( '.labels div', 0.75, { scale: 1, ease: Elastic.easeOut.config( 0.5 ) } ), 0 )
      tlSelection.add( TweenMax.to( '#progress', 0.75, { scale: 0.8, ease: Elastic.easeOut.config( 0.5 ) } ), 0 )
      this.timelines.push( tlSelection )
    },
    showInterstitial () {
      TweenMax.set( '.selection', { y: 50, opacity: 0 } )
      TweenMax.set( '.select-item', { y: 20, opacity: 0, rotationX: 20, rotationY: 50 } )
      let postTl = new TimelineMax()
      postTl.add( TweenMax.staggerTo( [ '.progress-container', '.labels' ], 0.5, {
        opacity: 0,
        ease: Power2.easeIn
      }, 0.075 ) )
      postTl.add( TweenMax.to( '.selection', 1, {
        y: 0,
        opacity: 1,
        ease: Power2.easeOut,
        onStart: this.$parent.showControls
      } ) )
      postTl.add( TweenMax.staggerTo( '.select-item', 1, {
        y: 0,
        opacity: 1,
        rotationX: 0,
        rotationY: 0,
        ease: Power2.easeOut
      }, 0.15 ), '-=0.5' )
      postTl.add( TweenMax.delayedCall( 3, this.showOutro ) )
      this.timelines.push(postTl)
    },
    showOutro () {
      this.outroActive = true
      this.$nextTick(() => {
        TweenMax.set( '.outro', { opacity: 0 } )
        TweenMax.set( '.outro-item', { opacity: 0, y: 25, rotationX: 20, rotationY: 50 } )
        let outroTl = new TimelineMax()
        outroTl.add( TweenMax.staggerTo( '.select-item', 0.75, {
          y: -20,
          opacity: 0,
          ease: Power3.easeIn
        }, 0.1 ) )
        outroTl.add( TweenMax.to( '.outro', 0.75, {
          opacity: 1,
          ease: Power2.easeOut
        } ) )
        outroTl.add( TweenMax.staggerTo( '.outro-item', 1, {
          y: 0,
          opacity: 1,
          rotationX: 0,
          rotationY: 0,
          ease: Power2.easeOut
        }, 0.15 ) )
        this.timelines.push( outroTl )
      })
    },
    showIntro () {
      this.$store.dispatch( 'enableDemo', this.idx )
      this.$store.dispatch( 'activateDemo', this.idx )
      TweenMax.staggerTo( '.intro-item', 1.5, {
        delay: 0.5,
        opacity: 1,
        y: 0,
        rotationX: 0,
        rotationY: 0,
        ease: Power3.easeOut
      }, 0.1 )
      TweenMax.delayedCall( this.introDelay, this.initLiquid )
      // this.tweenTargets.push( children )
      this.tweenTargets.push( this.initLiquid )
      this.tweenTargets.push( this.$refs.hint )
    },
    showHint () {
      TweenMax.to( this.$refs.hint, 1, {
        delay: 2,
        opacity: 1,
        y: 0,
        ease: Elastic.easeOut.config( 0.25 )
      } )
    },
    initLiquid () {
      this.iconActive = false

      this.colors.gradient1 = this.colorSets[0].secondary
      this.colors.gradient2 = this.colorSets[0].primary
      this.colors.gradient3 = this.colorSets[0].middleS
      this.colors.gradient4 = this.colorSets[0].middleP
      this.colors.gradient5 = this.colorSets[0].outerS
      this.colors.gradient6 = this.colorSets[0].outerP
      this.colorTweenCount++
      TweenMax.to( this.colors, 4, {
        colorProps: {
          gradient1: this.colorSets[this.colorTweenCount % this.colorSets.length].secondary,
          gradient2: this.colorSets[this.colorTweenCount % this.colorSets.length].primary,
          gradient3: this.colorSets[this.colorTweenCount % this.colorSets.length].middleS,
          gradient4: this.colorSets[this.colorTweenCount % this.colorSets.length].middleP,
          gradient5: this.colorSets[this.colorTweenCount % this.colorSets.length].outerS,
          gradient6: this.colorSets[this.colorTweenCount % this.colorSets.length].outerP
        },
        onComplete: ( t ) => {
          this.colorTweenCount++
          if ( this.colorTweenCount % 2 === 0 ) {
            t.updateTo({
              colorProps: {
                gradient1: this.colorSets[this.colorTweenCount % this.colorSets.length].secondary,
                gradient2: this.colorSets[this.colorTweenCount % this.colorSets.length].primary,
                gradient3: this.colorSets[this.colorTweenCount % this.colorSets.length].middleS,
                gradient4: this.colorSets[this.colorTweenCount % this.colorSets.length].middleP,
                gradient5: this.colorSets[this.colorTweenCount % this.colorSets.length].outerS,
                gradient6: this.colorSets[this.colorTweenCount % this.colorSets.length].outerP
              }
            }, true)
          } else {
            t.updateTo( {
              colorProps: {
                gradient1: this.colorSets[this.colorTweenCount % this.colorSets.length].primary,
                gradient2: this.colorSets[this.colorTweenCount % this.colorSets.length].secondary,
                gradient3: this.colorSets[this.colorTweenCount % this.colorSets.length].middleP,
                gradient4: this.colorSets[this.colorTweenCount % this.colorSets.length].middleS,
                gradient5: this.colorSets[this.colorTweenCount % this.colorSets.length].outerP,
                gradient6: this.colorSets[this.colorTweenCount % this.colorSets.length].outerS
              }
            }, true )
          }
          t.invalidate().play()
        },
        onCompleteParams: ['{self}']
      } )

      // this.baseX = this.fullWidth / 8
      this.transX = -this.fullWidth / 8

      TweenMax.to( this, 0.6, { transX: 0, ease: Power2.easeInOut } )
      // TweenMax.to(this, 0.6, {baseX: 0, delay: 0.05, ease: Power2.easeInOut})

      TweenMax.set( '.labels div', { opacity: 0, y: 20 } )

      TweenMax.to( this.$refs.intro, 0.6, {
        opacity: 0,
        ease: Power2.easeInOut,
        onComplete: () => {
          this.introActive = false
          this.triggerRipple( screen.availHeight - screen.availHeight / 4 )
          TweenMax.set( '.labels div', { rotationX: 20, rotationY: 50, transformOrigin: '50% 50%' } )
          TweenMax.staggerTo( '.labels div', 1, {
            opacity: 1,
            y: 0,
            rotationX: 0,
            rotationY: 0,
            ease: Power3.easeOut
          }, 0.15, () => {
            window.addEventListener( 'deviceorientation', this.rotationHandler )
            window.addEventListener( 'devicemotion', this.onDeviceMotion, false )
          } )
        }
      } )
      this.tweenTargets.push( this )
      this.tweenTargets.push( this.colors )
      this.tweenTargets.push( this.$refs.intro )
      this.tweenTargets.push( '.labels div' )
      this.onResize()
      this.update()
      this.showHint()
    },
    killAllTimelines () {
      this.timelines.forEach( tl => tl.kill() )
    }
  },
  created () {
    window.addEventListener( 'resize', this.onResize )
  },
  mounted () {
    // document.body.appendChild( this.stats.dom )
    TweenMax.set( '.intro-item', { opacity: 0, y: 20, rotationX: 20, rotationY: 50 } )
    TweenMax.set( this.$refs.hint, { opacity: 0, y: -100 } )
    this.ctx = this.$refs.canvas.getContext( '2d' )
    this.initCanvas()

    // Timeline Right Side
    this.tlRight.add( TweenMax.to( this.$refs.labelRight, 1, {
      x: -this.$refs.labelRight.clientWidth / 2,
      opacity: 1,
      scale: 1.2,
      ease: Power2.easeInOut
    } ) )

    this.tlRight.add( TweenMax.to( this.$refs.labelLeft, 1, {
      x: -this.$refs.labelLeft.clientWidth,
      opacity: 0.1,
      ease: Power2.easeInOut
    } ), 0 )

    // Timeline left Side
    this.tlLeft.add( TweenMax.to( this.$refs.labelLeft, 1, {
      x: this.$refs.labelLeft.clientWidth / 2,
      opacity: 1,
      scale: 1.2,
      ease: Power2.easeInOut
    } ) )

    this.tlLeft.add( TweenMax.to( this.$refs.labelRight, 1, {
      x: this.$refs.labelRight.clientWidth,
      opacity: 0.1,
      ease: Power2.easeInOut
    } ), 0 )

    this.timelines.push( this.tlLeft )
    this.timelines.push( this.tlRight )

    this.tweenTargets.push( '.intro-item' )
    this.$nextTick(() => {
      this.showIntro()
    })
  },
  beforeDestroy () {
    this.$store.dispatch( 'deactivateDemo', this.idx )
    TweenMax.killTweensOf( this.tweenTargets )
    this.killAllTimelines()
    this.cancelAutoRipple()
    window.removeEventListener( 'resize', this.onResize )
    window.removeEventListener( 'devicemotion', this.onDeviceMotion )
    window.removeEventListener( 'deviceorientation', this.rotationHandler )
    cancelAnimationFrame( this.rafID )
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/global/components/demo-common";

.demo {

  #liquid {
    position          : absolute;
    top               : -5px;
    left              : 0;
    width             : 100%;
    height            : 101%;
    transform-origin  : top center;
  }

  .labels {
    position    : absolute;
    top         : 50%;
    width       : 100%;
    font-family : $adobe-clean;
    font-size   : 1.6rem;
    line-height : 1.6rem;
    color       : $black;
    text-align  : center;
    transform   : translateY(-50px);

    .left {
      position  : absolute;
      left      : 0;
      width     : 50%;
      height    : 100px;
      opacity   : 0.75;
    }

    .right {
      position  : absolute;
      right     : 0;
      width     : 50%;
      height    : 100px;
      color     : $white;
      opacity   : 0.75;
    }
  }

  .progress-container {
    position  : absolute;
    top       : 0;
    left      : 0;
    width     : 100%;
    height    : 100%;

    .wrapper {
      width   : 100%;
      height  : 100%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/demos/DemoMotion.vue